<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('projects.imageChange')"></page-title>
    </div>

    <jet-form-section>

        <template #form>

            <modal-content>

                <div class="flex px-2 justify-between items-center pb-3 mb-4 border-b">

                    <h3>{{ $t("projects.uploadImage") }} <br>
                    <span class="text-sm text-gray-400">{{ $t("projects.fileInfo", { size: maxSizeInMb }) }}</span></h3>

                    <primary-button :disabled="!!image" type="button" @click.prevent="selectNewPhoto">
                        <CloudUploadIcon class="-ml-1 mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        {{ $t("options.addImage") }}
                    </primary-button>

                    <input type="file" class="hidden" ref="image" accept="image/png, image/jpeg, image/heic"
                           @change="upload" />

                </div>
                <div class="flex">
                    <project-image
                        v-if="image"
                        :image="image"
                        @deleteImage="deleteImage($event)"
                    />

                    <p v-if="!image" class="justify-self-center text-center w-full p-3 text-gray-500">
                        {{ $t("options.noImageUploaded") }}</p>
                </div>

            </modal-content>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">
                <primary-button class="ml-3 " @click="cancel">
                    {{ $t("actions.close") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import PageTitle from "@/Shared/Components/PageTitle";
import ModalContent from "@/Shared/Components/ModalContent";
import OptionImage from "@/Pages/Advisor/Option/Components/OptionImage";
import { CloudUploadIcon } from "@heroicons/vue/outline";
import ProjectImage from "@/Pages/Projects/Components/ProjectImage";

export default defineComponent({
    props: ["uploadLink", "project", "set"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        ProjectImage,
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetSecondaryButton,
        ModalContent,
        OptionImage,
        CloudUploadIcon
    },
    computed: {
        maxSizeInMb() {
            const bytes = this.$page.props.branding?.max_project_image_upload_size || (20 * 1024 * 1024);
            return Math.round(bytes / 1024 / 1024);
        }
    },
    data() {
        return {
            image: this.makeImage(this.project.image)
        };
    },
    methods: {
        makeImage(path){
            return path ? this.route("media.restore", { filename: path }) : null;
        },
        cancel() {
            this.$inertia.visit(this.route("advisors.projects.show", this.project.uuid), {
                preserveState: false
            });
        },
        deleteImage() {
            axios.delete(this.route("advisors.projects.delete-image", this.project.uuid)).then((rsp) => {
                this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "projects.imageRemoved"
                });
                this.image = null;
            }).catch(error => {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "actions.defaultError"
                });
            });
        },
        upload() {
            let formData = new FormData();
            formData.append("image", this.$refs.image.files[0]);

            const fsize = this.$refs.image.files[0].size;
            const maxBytes = this.$page.props.branding?.max_project_image_upload_size || (20 * 1024 * 1024);

            if (fsize > maxBytes) {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "projects.fileTooBig"
                });

                this.$refs.image.value = null;
                return;
            }

            axios.post(this.route("advisors.projects.upload-image", this.project.uuid), formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            }).then((rsp) => {
                this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "options.imageAdded"
                });
                this.image = this.makeImage(rsp.data.project.image)
                this.$refs.image.value = null;
            }).catch(error => {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "actions.defaultError"
                });
                this.$refs.image.value = null;
            });
        },
        selectNewPhoto() {
            this.$refs.image.click();
        }
    }

});
</script>

<style scoped></style>
